import { useState } from "react";
import { CheckCircle2, Circle, ChevronRight, ChevronDown, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOnboarding } from "@/hooks/useOnboarding";
import type { OnboardingStep } from "@/hooks/useOnboarding";
import { useGettingStartedDismissed } from "@/hooks/useGettingStartedDismissed";

const LS_KEY = "raise:getting-started:collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(LS_KEY) === "true";
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    localStorage.setItem(LS_KEY, String(value));
  } catch {
    // storage unavailable — degrade silently
  }
}

function StepRow({ step }: { step: OnboardingStep }) {
  const content = (
    <span className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-sm transition-colors hover:bg-muted/50">
      {step.completed ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
      ) : (
        <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <span className={step.completed ? "text-muted-foreground line-through" : ""}>
        {step.label}
      </span>
    </span>
  );

  if (step.action_url && !step.completed) {
    return <Link to={step.action_url}>{content}</Link>;
  }

  return content;
}

export function OnboardingChecklist() {
  const { onboarding, loading } = useOnboarding();
  const { dismissed, dismiss } = useGettingStartedDismissed();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  if (loading) {
    return <p className="mb-4 text-sm text-muted-foreground">Loading...</p>;
  }

  if (!onboarding || onboarding.all_complete || dismissed) {
    return null;
  }

  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
  }

  if (collapsed) {
    return (
      <button
        onClick={toggle}
        aria-label="Expand Getting Started checklist"
        className="mb-4 flex w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
      >
        <ChevronRight className="h-4 w-4 shrink-0" />
        <span>Getting Started</span>
        <span className="ml-auto text-xs">
          {onboarding.completed_count}/{onboarding.total}
        </span>
      </button>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Getting started</CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {onboarding.completed_count}/{onboarding.total} complete
            </span>
            <button
              onClick={toggle}
              aria-label="Collapse Getting Started checklist"
              className="rounded p-0.5 text-muted-foreground hover:bg-muted/50 transition-colors"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            <button
              onClick={dismiss}
              aria-label="Dismiss Getting Started checklist"
              className="rounded p-0.5 text-muted-foreground hover:bg-muted/50 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all"
            style={{
              width: `${(onboarding.completed_count / onboarding.total) * 100}%`,
            }}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-0.5 pb-3">
        {onboarding.steps.map((step) => (
          <StepRow key={step.id} step={step} />
        ))}
      </CardContent>
    </Card>
  );
}
